import React, { useEffect, useRef, useState } from 'react';
import { secureLogger } from '../utils/secureLogger';

interface PhotoLocationMapProps {
  latitude: number;
  longitude: number;
  heading?: number | null;
  height?: number;
}

const normalizeHeading = (heading: number) => {
  const value = heading % 360;
  return value < 0 ? value + 360 : value;
};

const createHeadingIcon = (heading: number) => {
  const rotation = normalizeHeading(heading);
  const svg = `
    <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <g transform="rotate(${rotation}, 20, 20)">
        <path d="M20 4 L30 28 L20 22 L10 28 Z" fill="#ff4d4f" stroke="#ffffff" stroke-width="2"/>
        <circle cx="20" cy="20" r="2.5" fill="#111111"/>
      </g>
    </svg>
  `;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(40, 40),
    anchor: new window.google.maps.Point(20, 20),
  };
};

const createCenterIcon = () => {
  const svg = `
    <svg width="22" height="22" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg">
      <circle cx="11" cy="11" r="8" fill="#1890ff" stroke="#ffffff" stroke-width="2"/>
    </svg>
  `;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(22, 22),
    anchor: new window.google.maps.Point(11, 11),
  };
};

const PhotoLocationMap: React.FC<PhotoLocationMapProps> = ({
  latitude,
  longitude,
  heading,
  height = 240,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const centerMarkerRef = useRef<any>(null);
  const headingMarkerRef = useRef<any>(null);
  const [error, setError] = useState<string | null>(null);

  const renderMap = () => {
    if (!mapRef.current || !window.google || !window.google.maps) {
      return;
    }

    const center = { lat: latitude, lng: longitude };
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        zoom: 17,
        center,
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        rotateControl: false,
        tiltControl: false,
      });
    } else {
      mapInstanceRef.current.setCenter(center);
    }

    if (centerMarkerRef.current) {
      centerMarkerRef.current.setMap(null);
    }
    if (headingMarkerRef.current) {
      headingMarkerRef.current.setMap(null);
    }

    centerMarkerRef.current = new window.google.maps.Marker({
      position: center,
      map: mapInstanceRef.current,
      icon: createCenterIcon(),
      zIndex: 1000,
    });

    if (Number.isFinite(heading)) {
      headingMarkerRef.current = new window.google.maps.Marker({
        position: center,
        map: mapInstanceRef.current,
        icon: createHeadingIcon(heading as number),
        zIndex: 1100,
      });
    }
  };

  useEffect(() => {
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      return;
    }

    if (window.google && window.google.maps) {
      setError(null);
      renderMap();
      return;
    }

    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey || apiKey === 'YOUR_GOOGLE_MAPS_API_KEY_HERE') {
      setError('Google Maps APIキーが設定されていません');
      return;
    }

    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      let attempts = 0;
      const maxAttempts = 50;
      const waitForMaps = window.setInterval(() => {
        attempts += 1;
        if (window.google && window.google.maps) {
          window.clearInterval(waitForMaps);
          setError(null);
          renderMap();
        } else if (attempts >= maxAttempts) {
          window.clearInterval(waitForMaps);
          setError('Google Maps APIの読み込みがタイムアウトしました');
        }
      }, 100);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry`;
    script.async = true;
    script.onload = () => {
      setError(null);
      renderMap();
    };
    script.onerror = () => {
      secureLogger.error('Failed to load Google Maps API');
      setError('Google Maps APIの読み込みに失敗しました');
    };
    document.head.appendChild(script);
  }, [latitude, longitude, heading]);

  if (error) {
    return (
      <div
        style={{
          width: '100%',
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 8,
          background: '#f5f5f5',
          color: '#999',
          fontSize: 12,
          border: '1px solid #e0e0e0',
        }}
      >
        {error}
      </div>
    );
  }

  return (
    <div
      ref={mapRef}
      style={{
        width: '100%',
        height,
        borderRadius: 8,
        border: '1px solid #e0e0e0',
      }}
    />
  );
};

export default PhotoLocationMap;
